import { useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const CALF_GREEN = '#8ebf63';
const ROW_ALT    = '#f7faf3';
const ROW_HOVER  = '#e8f4d9';

const money = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '';
  return '$ ' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '';
  return n.toLocaleString('es-AR');
};

// Vista macro de tarifas: agrupa por grupo macro (total) y despliega el detalle
// por tarifa. Datos desde /tarifagrupo/vista (todo el periodo, sin paginar).
const GrillaTarifasAgrupada = ({ tipo = 'vencimiento' }) => {
  const base = `${process.env.REACT_APP_API_URL}/tarifagrupo`;
  const [data, setData] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [periodo, setPeriodo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token'),
  };

  // cargar periodos disponibles y elegir el mas reciente
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${base}/periodos?tipo=${tipo}`, { headers: authHeaders });
        const j = await r.json();
        setPeriodos(Array.isArray(j) ? j : []);
        if (Array.isArray(j) && j.length && !periodo) setPeriodo(j[0]);
      } catch (e) { console.error('periodos', e); }
    })();
  }, [tipo]);

  // cargar la vista macro del periodo elegido
  useEffect(() => {
    if (!periodo) return;
    (async () => {
      setIsLoading(true);
      try {
        const r = await fetch(`${base}/vista?tipo=${tipo}&periodo=${encodeURIComponent(periodo)}`, { headers: authHeaders });
        const j = await r.json();
        setData(Array.isArray(j) ? j : []);
      } catch (e) { console.error('vista', e); setData([]); }
      setIsLoading(false);
    })();
  }, [periodo, tipo]);

  const columns = useMemo(() => [
    { accessorKey: 'grupo', header: 'Grupo', GroupedCell: ({ cell, row }) => (
        <Box sx={{ fontWeight: 'bold' }}>{cell.getValue()} ({row.subRows?.length ?? 0})</Box>
      ) },
    { accessorKey: 'categoria', header: 'Tarifa' },
    {
      accessorKey: 'importe', header: 'Importe',
      aggregationFn: 'sum',
      AggregatedCell: ({ cell }) => <Box sx={{ fontWeight: 'bold' }}>{money(cell.getValue())}</Box>,
      Cell: ({ cell }) => money(cell.getValue()),
      muiTableBodyCellProps: { align: 'right' },
      muiTableHeadCellProps: { align: 'right' },
    },
    {
      accessorKey: 'consumo', header: 'Consumo',
      aggregationFn: 'sum',
      AggregatedCell: ({ cell }) => <Box sx={{ fontWeight: 'bold' }}>{num(cell.getValue())}</Box>,
      Cell: ({ cell }) => num(cell.getValue()),
      muiTableBodyCellProps: { align: 'right' },
      muiTableHeadCellProps: { align: 'right' },
    },
    {
      accessorKey: 'cantidad', header: 'Cant. Comprobantes',
      aggregationFn: 'sum',
      AggregatedCell: ({ cell }) => <Box sx={{ fontWeight: 'bold' }}>{num(cell.getValue())}</Box>,
      Cell: ({ cell }) => num(cell.getValue()),
      muiTableBodyCellProps: { align: 'right' },
      muiTableHeadCellProps: { align: 'right' },
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data,
    enableGrouping: true,
    enableExpanding: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableColumnDragging: false,
    positionToolbarAlertBanner: 'none',
    state: { isLoading },
    initialState: {
      density: 'compact',
      grouping: ['grupo'],
      expanded: true,
      sorting: [{ id: 'importe', desc: true }],
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#f5f5f5', color: '#161616', fontWeight: 'bold',
        fontSize: '0.85rem', borderBottom: `3px solid ${CALF_GREEN}`,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.getIsGrouped() ? CALF_GREEN + '22' : (row.index % 2 === 0 ? '#ffffff' : ROW_ALT),
        '&:hover td': { backgroundColor: ROW_HOVER },
      },
    }),
    muiTableBodyCellProps: { sx: { fontSize: '0.85rem', borderBottom: '1px solid #eee' } },
    muiTableContainerProps: { sx: { overflowX: 'auto', maxWidth: '100%' } },
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Período" value={periodo}
          onChange={(e) => setPeriodo(e.target.value)} sx={{ minWidth: 140 }}
        >
          {periodos.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          Total por grupo macro — expandí para ver el detalle de tarifas.
        </Typography>
      </Box>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default GrillaTarifasAgrupada;
